import type {
  DownloadComplete,
  DownloadNotComplete,
  DownloadRequestBody,
} from 'types/isomorphic/downloadRequest.types';
import type { ImageSize } from 'types/isomorphic/contentGenRequest.types';
import type {
  AssetDownloadAgreementsResponse,
  BatchDownloadResponse,
  LicensedDownloadResponse,
  SaveDownloadPreferencesRequest,
  SingleDownloadResponse,
} from 'types/isomorphic/download.types';
import { Axios } from 'axios';
import apiUrl from '../apiUrl';

export class DownloadService {
  #httpClient: Axios;
  baseUrl: string;

  constructor(httpClient: Axios) {
    this.#httpClient = httpClient;
    this.baseUrl = `${apiUrl}/downloads`;
  }

  createSingleDownload = async (
    token: string,
    notes: string,
    projectCode: string,
    modificationOptions?: { assetId?: string; customerId?: string }
  ): Promise<SingleDownloadResponse | Record<string, never>> => {
    try {
      const url = `${this.baseUrl}/create-download`;
      const { assetId, customerId } = modificationOptions || {};

      const response = await this.#httpClient.post<SingleDownloadResponse>(
        url,
        {
          data: {
            tokens: [token],
            notes,
            projectCode,
            assetId,
            customerId,
          },
        }
      );
      return response.data;
    } catch (e) {
      return {};
    }
  };

  createBatchDownload = async (
    tokens: string[],
    notes: string,
    projectCode: string,
    korrelationId: string
  ): Promise<BatchDownloadResponse | Record<string, never>> => {
    const data = {
      projectCode,
      notes,
      tokens,
      korrelationId,
    };

    const url = `${this.baseUrl}/create-download`;
    const response = await this.#httpClient.post<BatchDownloadResponse>(url, {
      data,
    });
    if (response.data.multiDownloads) {
      const newMulti = response.data.multiDownloads.filter(
        (item) => item.isZip === true
      );
      const newNotZipped = response.data.multiDownloads.filter(
        (item) => item.isZip === false
      );
      return {
        downloadDestinations: response.data.downloadDestinations,
        multiDownloads: newMulti,
        multiDownloadsNotZipped: newNotZipped,
        numAssets: response.data.multiDownloads.length,
        unauthorizedAssets: response.data.unauthorizedAssets,
      };
    }

    return {};
  };

  getDownloadAgreements = async (
    assetId: string
  ): Promise<AssetDownloadAgreementsResponse | Record<string, never>> => {
    try {
      const url = `${this.baseUrl}/download-agreements/${assetId}`;
      const response =
        await this.#httpClient.get<AssetDownloadAgreementsResponse>(url);
      return response.data;
    } catch (e) {
      return {};
    }
  };

  sendToMam = async (assetId: string): Promise<boolean> => {
    try {
      const url = `${this.baseUrl}/send-to-mam/${assetId}`;
      const response = await this.#httpClient.post(url);
      return response.status === 200;
    } catch (e) {
      return false;
    }
  };

  saveDownloadPreferences = async (
    preferences: Record<string, string>
  ): Promise<boolean> => {
    try {
      const url = `${this.baseUrl}/preferences`;
      const body: SaveDownloadPreferencesRequest = { preferences };
      const response = await this.#httpClient.post(url, body);
      return response.status === 200;
    } catch (e) {
      return false;
    }
  };

  getLicensedDownloadUrl = async (
    assetId: string,
    orderId: number,
    orderDetailId: number,
    sizeName: string
  ): Promise<LicensedDownloadResponse | Record<string, never>> => {
    try {
      const url = `${this.baseUrl}/licensed-redownload`;
      const response = await this.#httpClient.post<LicensedDownloadResponse>(
        url,
        {
          assetId,
          orderId,
          orderDetailId,
          sizeName,
        }
      );
      return response.data;
    } catch (e) {
      return {};
    }
  };

  getImageDownloadSizes: (
    genRequestId: string,
    index: number
  ) => Promise<ImageSize[]> = async (
    genRequestId: string,
    index: number
  ): Promise<ImageSize[]> => {
    const url = `${this.baseUrl}/download-sizes`;
    const response = await this.#httpClient.post<ImageSize[]>(url, {
      genRequestId,
      index,
    });
    return response.data;
  };

  /**
   * initiateDownload will let the service know which specific image
   * will be downloaded, starting the process of upscaling/downloading
   */
  initiateDownload = async (
    args: DownloadRequestBody
  ): Promise<DownloadComplete | DownloadNotComplete> => {
    try {
      const response = await this.#httpClient.post<
        DownloadComplete | DownloadNotComplete
      >(`${this.baseUrl}/custom-media-download`, args);
      return response.data;
    } catch (error) {
      return await Promise.reject(error);
    }
  };

  /**
   * pollForDownload checks periodically if the download is ready
   * once is ready it will return data with status 200 and download URL string
   */
  pollForDownload = async (
    genRequestId: string,
    index: number
  ): Promise<DownloadComplete | DownloadNotComplete> => {
    const url = `${this.baseUrl}/custom-media-download/${genRequestId}/${index}`;
    try {
      const response = await this.#httpClient.get<
        DownloadComplete | DownloadNotComplete
      >(url);
      return response.data;
    } catch (error) {
      return await Promise.reject(error);
    }
  };
}

export default DownloadService;
